import * as React from "react";
import { useDispatch } from "@teamprevent/admin-redux";
import { Modal, Button } from "semantic-ui-react";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { MedicalTypeIds } from "@teamprevent/common/healthSurveillance";

import { sendHealthSurveillanceQuestionnaire } from "../../healthSurveillance/actions";
import { SEND_HEALTH_SURVEILLANCE_QUESTIONNAIRE } from "../../healthSurveillance/actionTypes";
import { useOhCaseDetailsModel } from "../../crud/ohCase/hooks";

import { CompleteNowHealthSurveillanceQuestionnaireButton } from "./CompleteNowHealthSurveillanceQuestionnaireButton";

interface Props {
    task: TaskModel;
}

export const SendHealthSurveillanceQuestionnaireButton: React.FunctionComponent<Props> = (props) => {
    const { ohCase, isLoading: isOhCaseLoading, isLoaded } = useOhCaseDetailsModel({
        customerId: props.task.customer.id,
        ohCaseId: props.task.caseId
    });
    const [showHAVSModal, setShowHAVSModal] = React.useState(false);
    const dispatch = useDispatch();
    const onModalClose = React.useCallback(() => setShowHAVSModal(false), []);
    const sendQuestionnaire = React.useCallback(async (consultationRequired?: boolean) => {
        onModalClose();
        await dispatch(sendHealthSurveillanceQuestionnaire(props.task.customer.id, props.task.caseId, props.task.id, consultationRequired));
    }, [props.task, dispatch, onModalClose]);
    const onSendQuestionnaireNoConsultationClick = React.useCallback(() => sendQuestionnaire(false), [sendQuestionnaire]);
    const confirmBookF2FHAVSAssessment = React.useCallback(() => sendQuestionnaire(true),[sendQuestionnaire]);
    const isQuestionnaireOnly = isLoaded && ohCase.isQuestionnaireOnly;
    const medicalTypes = React.useMemo(() => ohCase?.medicalTypes ?? [], [ohCase]);
    const onSendQuestionnaireClick = React.useCallback(() => {
        if (isLoaded === false) { return; }
        if (
            isQuestionnaireOnly && (medicalTypes.includes(MedicalTypeIds.HAVS) || medicalTypes.includes(MedicalTypeIds.HAVSFaceToFace))
        ) {
            setShowHAVSModal(true);
        } else {
            sendQuestionnaire();
        }
    },[isLoaded, isQuestionnaireOnly, medicalTypes, sendQuestionnaire]);
    if (props.task.completed || props.task.cancelled) {
        return null;
    }

    return (
        <>
            <CompleteNowHealthSurveillanceQuestionnaireButton task={props.task} />
            <IsLoadingButton
                content="Send"
                onClick={onSendQuestionnaireClick}
                requestNames={SEND_HEALTH_SURVEILLANCE_QUESTIONNAIRE}
                floated="right"
                loading={isOhCaseLoading}
            />
            <Modal open={showHAVSModal} size="small">
                <Modal.Header content="Do you need to book a face-to-face HAVS assessment now?" />
                <Modal.Actions>
                    <IsLoadingButton
                        primary
                        content="Yes"
                        onClick={confirmBookF2FHAVSAssessment}
                        requestNames={[SEND_HEALTH_SURVEILLANCE_QUESTIONNAIRE]}
                    />
                    <IsLoadingButton
                        primary
                        content="No"
                        onClick={onSendQuestionnaireNoConsultationClick}
                        requestNames={SEND_HEALTH_SURVEILLANCE_QUESTIONNAIRE}
                    />
                    <Button onClick={onModalClose}>Cancel</Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};
